/*
  <capra-filters>: the collection's Colour and Collection filters work on the spot
  (owner, 2026-10-16). Every card says what it matches (data-match, snippets/match-data;
  on a grouped card each colour dot has its own), so choosing a colour or a collection
  hides the cards that do not match at once, turns a grouped card to the chosen colour,
  keeps the choice in the address (?colour=…&in=…), so a filtered
  view can be shared and survives a change of sort order (which reloads the page).
  Without this script the colour links open the colour's own page, and the form's
  Apply button submits the sort.
*/
class CapraFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-filter-form]');
    this.grid = document.querySelector('.grid');
    if (!this.form) return;
    this.classList.add('is-live');
    this.live = this.querySelector('[data-live]');
    // A page already filtered on the server (a colour's own page) keeps its links as they are.
    this.served = Boolean(this.querySelector('[data-server-active]'));
    const params = new URLSearchParams(location.search);
    this.state = { colour: params.get('colour') || '', in: params.get('in') || '' };

    this.form.addEventListener('change', (event) => {
      if (!event.target.matches('[data-sort]')) return;
      this.carry();
      this.form.requestSubmit();
    });

    this.addEventListener('click', (event) => {
      const colour = event.target.closest('[data-colour]');
      const group = event.target.closest('[data-in]');
      const clear = event.target.closest('[data-clear-filter]');
      // A pill is a link: a new tab or window is left to the browser
      if (group && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button)) return;
      if (clear) {
        this.state[clear.dataset.clearFilter] = '';
      } else if (colour && !this.served) {
        this.state.colour = this.state.colour === colour.dataset.colour ? '' : colour.dataset.colour;
      } else if (group) {
        this.state.in = this.state.in === group.dataset.in ? '' : group.dataset.in;
      } else {
        return;
      }
      event.preventDefault();
      event.target.closest('details')?.removeAttribute('open');
      this.apply();
    });

    // A list left open closes on Esc (focus back on its label) or a click elsewhere.
    this.addEventListener('keydown', (event) => {
      const open = event.key === 'Escape' && event.target.closest('details.pick[open]');
      if (!open) return;
      open.removeAttribute('open');
      open.querySelector('summary').focus();
    });
    document.addEventListener('pointerdown', (event) => {
      for (const open of this.querySelectorAll('details.pick[open]')) if (!open.contains(event.target)) open.removeAttribute('open');
    });

    if (this.state.colour || this.state.in) this.apply();
  }

  matches(text) {
    const { colour, in: group } = this.state;
    return (!colour || text.includes(`|${colour}|`)) && (!group || text.includes(`|${group}|`));
  }

  apply() {
    let shown = 0;
    for (const item of this.grid ? this.grid.children : []) {
      const dots = [...item.querySelectorAll('.dot[data-match]')];
      let show;
      if (dots.length) {
        const hits = dots.filter((dot) => this.matches(dot.dataset.match));
        show = hits.length > 0;
        // A grouped card turns to the chosen colour (capra-model-card.js swaps it at once). Only once that
        // script is defined: on a shared filtered link this runs first, and the click would open the dot's link.
        if (show && this.state.colour && !hits.some((dot) => dot.classList.contains('is-active'))) {
          customElements.whenDefined('capra-model-card').then(() => hits[0].click());
        }
      } else {
        show = this.matches(item.dataset.match || '');
      }
      item.hidden = !show;
      if (show) shown += 1;
    }
    this.mark();
    this.say(shown);
    const url = new URL(location.href);
    for (const key of ['colour', 'in']) {
      if (this.state[key]) url.searchParams.set(key, this.state[key]);
      else url.searchParams.delete(key);
    }
    history.replaceState(history.state, '', url);
  }

  // The chosen option looks chosen
  mark() {
    for (const link of this.querySelectorAll('[data-colour]')) link.toggleAttribute('aria-current', !this.served && link.dataset.colour === this.state.colour);
    for (const option of this.querySelectorAll('[data-in]')) option.toggleAttribute('aria-current', option.dataset.in === this.state.in);
    this.markColour();
  }

  // The Colour capsule turns into the chosen colour (its swatch and name, a cross to clear it) or back
  markColour() {
    const choice = this.querySelector('[data-colour-choice]');
    if (!choice || this.served) return;
    const { colour } = this.state;
    const summary = choice.querySelector('summary');
    const swatch = choice.querySelector('[data-choice-swatch]');
    const clear = choice.querySelector('[data-clear-filter]');
    const source = colour && this.querySelector(`[data-colour="${CSS.escape(colour)}"] .pick__swatch`);
    choice.classList.toggle('is-on', Boolean(colour));
    choice.querySelector('[data-choice-name]').textContent = colour || choice.dataset.label;
    swatch.hidden = !source;
    if (source) swatch.style.setProperty('--swatch', source.style.getPropertyValue('--swatch'));
    clear.hidden = !colour;
    if (colour) {
      summary.setAttribute('aria-label', `${choice.dataset.label}: ${colour}`);
      clear.setAttribute('aria-label', `${clear.dataset.clearLabel}: ${colour}`);
    } else {
      summary.removeAttribute('aria-label');
    }
  }

  // Only an empty result needs words: a pressed pill and the filled Colour capsule show the rest
  say(shown) {
    if (!this.live) return;
    const items = [];
    // No count (owner, 2026-10-16); only an empty result says so
    if (shown === 0) items.push(Object.assign(document.createElement('span'), { className: 'tools__empty', textContent: this.form.dataset.empty }));
    this.live.hidden = items.length === 0;
    this.live.replaceChildren(...items);
  }

  // A new sort order reloads the page: the filters go with it
  carry() {
    for (const key of ['colour', 'in']) {
      this.form.querySelector(`input[name="${key}"]`)?.remove();
      if (!this.state[key]) continue;
      const input = Object.assign(document.createElement('input'), { type: 'hidden', name: key, value: this.state[key] });
      this.form.append(input);
    }
  }
}

if (!customElements.get('capra-filters')) customElements.define('capra-filters', CapraFilters);
